import * as React from 'react';
import {Container} from '@inlet/react-pixi';
import {Rectangle} from '@gongxigames/framework/core/fragments/react';
import {InteractionEvent, Point} from 'pixi.js';

type SliderProps = {
    min: number;
    max: number;
    width: number;
    height: number;
    onChange: (value: number) => void;
    step?: number;
    scaleColor?: number;
    filledScaleColor?: number;
    thumbColor?: number;
    thumbSize?: number;
    value?: number;
    x?: number;
    y?: number;
};

type SliderState = {
    isDownThumb: boolean;
    oldPoint?: Point;
};

export class Slider extends React.Component<SliderProps, SliderState> {
    public state: SliderState = {
        isDownThumb: false
    };

    protected onPointerDownThumb(e: InteractionEvent): void {
        this.setState({isDownThumb: true, oldPoint: e.data.global.clone() });
    }

    protected onPointerUpThumb(): void {
        this.setState({isDownThumb: false, oldPoint: undefined});
    }

    protected onPointerMoveThumb(e: InteractionEvent): void {
        if (this.state.isDownThumb && this.state.oldPoint) {
            const oldValue = this.props.value || 0;
            const oldPoint = this.state.oldPoint;
            const deltaPosition = e.data.global.x - oldPoint.x;
            let newPosition = this.getThumbPositionByValue() + deltaPosition;

            // set a value in the range from 0 to 100
            if (newPosition < 0) {
                newPosition = 0;
            } else if (newPosition > this.props.width) {
                newPosition = this.props.width;
            }

            let newValue = this.getValueByThumbPosition(newPosition);
            const deltaValue = round(newValue - oldValue);

            // align to step size
            if (this.props.step) {
                if (Math.abs(deltaValue) < this.props.step) {
                    return;
                }

                const rest = deltaValue % this.props.step;

                newValue = oldValue + (deltaValue - rest);

            }

            this.props.onChange(round(newValue));
            this.setState({oldPoint: e.data.global.clone()});
        }
    }

    protected getValueByThumbPosition(position: number): number {
        const {min, max, width} = this.props;

        return min + ((Math.abs(min) + max) * (position / width));
    }

    protected getThumbPositionByValue(): number {
        const {min, max, width, value = min} = this.props;
        const position = (Math.abs(min) + value) / (max + Math.abs(min));

        return width * position;
    }

    protected getPropsToContainer(): any {
        const props: any = {};
        const propsList = ['x', 'y'];

        propsList.forEach(key => {
            const value = (this.props as any)[key];

            if (value !== undefined) {
                props[key] = value;
            }
        });

        return props;
    }

    public render(): JSX.Element {
        const {
            width,
            height = 20,
            scaleColor = 0x00ff00,
            filledScaleColor = 0xff0000,
            thumbColor = 0x0000ff,
            thumbSize = 40
        } = this.props;

        const thumbPosition = this.getThumbPositionByValue();
        const halfHeight = height / 2;

        return (
            <Container {...this.getPropsToContainer()}>
                <Rectangle width={width} height={height} fill={scaleColor}/>
                <Rectangle width={thumbPosition} height={height} fill={filledScaleColor} />
                <Rectangle x={thumbPosition - halfHeight}
                    y={halfHeight - (thumbSize / 2)}
                    width={thumbSize}
                    height={thumbSize}
                    fill={thumbColor}
                    interactive={true}
                    buttonMode={true}
                    pointerdown={this.onPointerDownThumb.bind(this)}
                    pointerup={this.onPointerUpThumb.bind(this)}
                    pointerupoutside={this.onPointerUpThumb.bind(this)}
                    pointermove={this.onPointerMoveThumb.bind(this)}
                />
            </Container>
        );
    }
}

function round(value: number): number {
    return Math.round(value * 100) / 100;
}
